import { FC, ReactNode, useMemo, useRef, useState } from "react";
import ReactECharts from "echarts-for-react";
import type { EChartsOption } from "echarts";
import { ECHARTS_THEME } from "../../echartsTheme";
import { Box, Typography } from "@mui/material";

import CoreCard from "../../../ui/core/CoreCard";
import UsageDownloadCsv from "./UsageDownloadCsv";
import { downloadLocalCsv, type CsvRowValue } from "./downloadLocalCsv";

export interface PieSlice {
  name: string;
  value: number;
  color?: string;
}

interface PieChartCardProps {
  title: string;
  slices: PieSlice[];
  formatValue?: (value: number) => string;
  csvFilename: string;
  csvValueHeader?: string;
  emptyMessage?: string;
  isLoading?: boolean;
  // Optional controls (e.g. a view toggle) rendered in the title bar.
  controls?: ReactNode;
  // Render a full pie instead of the default donut.
  fullPie?: boolean;
  /** When true, CSV includes only legend-selected (visible) slices. */
  csvSelectedOnly?: boolean;
}

const defaultFormat = (value: number): string => `${value}`;

const buildOption = (
  slices: PieSlice[],
  formatValue: (value: number) => string,
  fullPie: boolean,
): EChartsOption => ({
  tooltip: {
    trigger: "item",
    formatter: (params: unknown) => {
      const p = params as {
        name?: string;
        value?: number;
        percent?: number;
        marker?: string;
      };
      return `${p.marker ?? ""} ${p.name ?? ""}: <b>${formatValue(
        Number(p.value ?? 0),
      )}</b> (${(p.percent ?? 0).toFixed(1)}%)`;
    },
  },
  legend: { bottom: 0, type: "scroll" },
  series: [
    {
      type: "pie",
      radius: fullPie ? ["0%", "70%"] : ["40%", "68%"],
      center: ["50%", "44%"],
      avoidLabelOverlap: true,
      label: { show: false },
      labelLine: { show: false },
      data: slices.map((s) => ({
        name: s.name,
        value: s.value,
        ...(s.color ? { itemStyle: { color: s.color } } : {}),
      })),
    },
  ],
});

/**
 * Reads legend selection from the live chart (for `csvSelectedOnly` CSV export).
 * Assumes a single legend from `buildOption`; uses `legend[0]` when ECharts returns an array.
 * Update this if `buildOption` gains multiple legends.
 */
const getLegendSelected = (
  chart: ReturnType<ReactECharts["getEchartsInstance"]> | undefined,
): Record<string, boolean> | undefined => {
  if (!chart) {
    return undefined;
  }

  const option = chart.getOption() as EChartsOption;
  const legend = option.legend;
  const legendConfig = Array.isArray(legend) ? legend[0] : legend;
  return legendConfig?.selected as Record<string, boolean> | undefined;
};

const PieChartCard: FC<PieChartCardProps> = ({
  title,
  slices,
  formatValue = defaultFormat,
  csvFilename,
  csvValueHeader = "Value",
  emptyMessage,
  isLoading,
  controls,
  fullPie = false,
  csvSelectedOnly = false,
}) => {
  const chartRef = useRef<ReactECharts>(null);
  const [legendSelectionRevision, setLegendSelectionRevision] = useState(0);
  const hasData = slices.some((s) => s.value > 0);
  const option = useMemo(
    () => buildOption(slices, formatValue, fullPie),
    [slices, formatValue, fullPie],
  );

  const csvData = useMemo((): CsvRowValue[][] => {
    if (!csvSelectedOnly) {
      return slices.map((s) => [s.name, s.value]);
    }

    const selected = getLegendSelected(chartRef.current?.getEchartsInstance());
    if(!selected) {
      return slices.map((s) => [s.name, s.value]);
    }

    return slices
      .filter((slice) => selected?.[slice.name] !== false)
      .map((s) => [s.name, s.value]);
  }, [csvSelectedOnly, legendSelectionRevision, slices]);

  const chartEvents = useMemo(
    () =>
      csvSelectedOnly
        ? { // Re-render the chart when the legend is selected
            legendselectchanged: () => {
              setLegendSelectionRevision((revision) => revision + 1);
            },
          }
        : undefined,
    [csvSelectedOnly],
  );

  const titleBar = (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        marginBottom: 1.5,
      }}
    >
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{
          flexShrink: 0,
          textTransform: "uppercase",
          letterSpacing: 0.5,
          fontWeight: 600,
        }}
      >
        {title}
      </Typography>
      {controls}
      <Box sx={{ flex: 1 }} />
      {hasData ? (
        <UsageDownloadCsv
          onDownload={() =>
            downloadLocalCsv(["Category", csvValueHeader], csvData, csvFilename)
          }
          disabled={csvData.length === 0}
        />
      ) : null}
    </Box>
  );

  return (
    <CoreCard
      isLoading={isLoading}
      sx={{ display: "flex", flexDirection: "column", height: "100%" }}
    >
      {titleBar}
      {hasData ? (
        <Box sx={{ flex: 1, minHeight: 0 }}>
          <ReactECharts
            ref={chartRef}
            option={option}
            theme={ECHARTS_THEME}
            style={{ height: "100%", width: "100%" }}
            notMerge
            lazyUpdate
            onEvents={chartEvents}
          />
        </Box>
      ) : (
        <Box
          sx={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography variant="body2" color="text.secondary">
            {emptyMessage ?? "No data available"}
          </Typography>
        </Box>
      )}
    </CoreCard>
  );
};

export default PieChartCard;
